import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { creditoSospeso, pronteOttemperanza, cuDaRecuperare, successFeeNonPagate, scadenzeLiquidazione } from '../api';

export default function Finanziario() {
  const [tab, setTab] = useState('cruscotto');
  const [carico, setCarico] = useState(true);
  const [dati, setDati] = useState({
    credito: [],
    ottemperanze: [],
    cu: null,
    successFee: null,
    scadenze: []
  });

  useEffect(() => {
    caricaDati();
  }, []);

  const caricaDati = async () => {
    setCarico(true);
    try {
      const [cr, ott, cu, sf, sc] = await Promise.all([
        creditoSospeso(),
        pronteOttemperanza(),
        cuDaRecuperare(),
        successFeeNonPagate(),
        scadenzeLiquidazione(30)
      ]);
      setDati({
        credito: cr.data,
        ottemperanze: ott.data,
        cu: cu.data,
        successFee: sf.data,
        scadenze: sc.data
      });
    } catch (e) {
      console.error(e);
    } finally {
      setCarico(false);
    }
  };

  const totaleCredito = dati.credito.reduce((s, r) => s + (r.credito_residuo || 0), 0);
  const totaleSF = dati.successFee?.totale_success_fee_da_incassare || 0;
  const totaleCU = dati.cu?.totale_cu_da_recuperare || 0;

  const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const tabs = [
    { id: 'cruscotto', label: 'Cruscotto' },
    { id: 'ottemperanze', label: 'Pronte ottemperanza' },
    { id: 'scadenze', label: 'Scadenze' },
    { id: 'success-fee', label: 'Success fee' },
    { id: 'cu', label: 'CU da recuperare' },
  ];

  if (carico) return (
    <div className="carico-box">
      <Loader size={32} className="spin" />
      <p>Caricamento rendicontazione...</p>
    </div>
  );

  return (
    <div className="pagina">
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        <div className="kpi-card" style={{ borderColor: '#fc8181' }}>
          <div className="kpi-val" style={{ color: '#c53030' }}>{fmt(totaleCredito)}</div>
          <div className="kpi-lbl">Credito sospeso totale</div>
          <div className="kpi-sub">{dati.credito.reduce((s,r) => s + r.num_sentenze, 0)} sentenze</div>
        </div>
        <div className="kpi-card" style={{ borderColor: '#f6ad55' }}>
          <div className="kpi-val" style={{ color: '#c05621' }}>{fmt(totaleSF)}</div>
          <div className="kpi-lbl">Success fee da incassare</div>
          <div className="kpi-sub">{dati.successFee?.dettaglio?.length || 0} clienti</div>
        </div>
        <div className="kpi-card" style={{ borderColor: '#68d391' }}>
          <div className="kpi-val" style={{ color: '#276749' }}>{fmt(totaleCU)}</div>
          <div className="kpi-lbl">CU anticipati da recuperare</div>
          <div className="kpi-sub">{dati.cu?.dettaglio?.length || 0} ricorsi</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mode-tabs" style={{ marginBottom: 16 }}>
        {tabs.map(t => (
          <button key={t.id} className={`mode-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Cruscotto */}
      {tab === 'cruscotto' && (
        <div>
          <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
            Credito sospeso per tipo ricorso
          </h4>
          <table className="fin-table">
            <thead>
              <tr>
                <th>Tipo ricorso</th>
                <th>Sentenze</th>
                <th>Importo spese</th>
                <th>Liquidato</th>
                <th>Residuo</th>
              </tr>
            </thead>
            <tbody>
              {dati.credito.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 24 }}>
                  Nessun credito sospeso
                </td></tr>
              ) : dati.credito.map((r, i) => (
                <tr key={i}>
                  <td>{r.tipo_ricorso || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{r.num_sentenze}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.totale_spese)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.totale_liquidato)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500, color: '#c53030' }}>{fmt(r.credito_residuo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pronte ottemperanza */}
      {tab === 'ottemperanze' && (
        <div>
          <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
            {dati.ottemperanze.length} sentenze pronte per ricorso di ottemperanza
          </h4>
          <table className="fin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Ricorso</th>
                <th>Tribunale</th>
                <th>N. Sentenza</th>
                <th>Data sentenza</th>
                <th>Importo spese</th>
                <th>Passaggio giudicato</th>
              </tr>
            </thead>
            <tbody>
              {dati.ottemperanze.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 24 }}>
                  Nessuna sentenza pronta per ottemperanza
                </td></tr>
              ) : dati.ottemperanze.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{r.cognome} {r.nome}</td>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{r.codice_ricorso}</td>
                  <td>{r.tribunale || '—'}</td>
                  <td>{r.numero_sentenza || '—'}</td>
                  <td>{fmtDate(r.data_sentenza)}</td>
                  <td style={{ textAlign: 'right', color: '#c53030', fontWeight: 500 }}>{fmt(r.importo_spese)}</td>
                  <td>{fmtDate(r.data_passaggio_giudicato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Scadenze */}
      {tab === 'scadenze' && (
        <div>
          <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
            Sentenze in scadenza nei prossimi 30 giorni
          </h4>
          <table className="fin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Ricorso</th>
                <th>N. Sentenza</th>
                <th>Importo spese</th>
                <th>Scadenza liquidazione</th>
                <th>Giorni alla scadenza</th>
              </tr>
            </thead>
            <tbody>
              {dati.scadenze.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 24 }}>
                  Nessuna scadenza nei prossimi 30 giorni
                </td></tr>
              ) : dati.scadenze.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{r.cognome} {r.nome}</td>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{r.codice_ricorso}</td>
                  <td>{r.numero_sentenza || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.importo_spese)}</td>
                  <td>{fmtDate(r.scadenza_liquidazione)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500,
                      background: r.giorni_alla_scadenza <= 7 ? '#FCEBEB' : '#FAEEDA',
                      color: r.giorni_alla_scadenza <= 7 ? '#791F1F' : '#633806'
                    }}>{r.giorni_alla_scadenza}gg</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Success fee */}
      {tab === 'success-fee' && (
        <div>
          <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
            {dati.successFee?.dettaglio?.length || 0} clienti con success fee da incassare — totale {fmt(totaleSF)}
          </h4>
          <table className="fin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>CF</th>
                <th>Ricorso</th>
                <th>Compenso</th>
                <th>Data sentenza</th>
                <th>Esito</th>
              </tr>
            </thead>
            <tbody>
              {(dati.successFee?.dettaglio || []).length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 24 }}>
                  Nessuna success fee da incassare
                </td></tr>
              ) : (dati.successFee?.dettaglio || []).map((r, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{r.cognome} {r.nome}</td>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{r.codice_fiscale}</td>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{r.codice_ricorso}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt(r.compenso)}</td>
                  <td>{fmtDate(r.data_sentenza)}</td>
                  <td><span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, background: '#EAF3DE', color: '#27500A' }}>{r.esito}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CU da recuperare */}
      {tab === 'cu' && (
        <div>
          <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
            {dati.cu?.dettaglio?.length || 0} ricorsi con CU da recuperare — totale {fmt(totaleCU)}
          </h4>
          <table className="fin-table">
            <thead>
              <tr>
                <th>Codice ricorso</th>
                <th>Grado</th>
                <th>TAR competente</th>
                <th>CU anticipato</th>
              </tr>
            </thead>
            <tbody>
              {(dati.cu?.dettaglio || []).length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 24 }}>
                  Nessun CU da recuperare
                </td></tr>
              ) : (dati.cu?.dettaglio || []).map((r, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.codice_ricorso}</td>
                  <td>{r.grado}</td>
                  <td>{r.tar_competente || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt(r.cu_anticipato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
